import type { APIStatus } from "@/api/APIStatus.type";
import { getCheapestItemScores } from "@/api/api";
import { canonicalUrl } from "@/config/canonical";
import { useSeoCopy } from "@/config/seo";
import { HeroSection } from "@/containers/HeroSection";
import { getUserSelector } from "@/redux/user/userSlice";
import type { CheapestItemScoreRow } from "@repo/shared/api";
import { useTranslation } from "@repo/shared/i18n";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { CheapestItemScoreTable } from "./components/CheapestItemScoreTable";

export const CheapestItemScorePage = () => {
  const { t } = useTranslation("cheapestItemScore");
  const seo = useSeoCopy("cheapestItemScore");
  // The backend prices rows for the signed-in platform, so a late-arriving
  // user (or a platform change) refetches.
  const platform = useSelector(getUserSelector).user.platform;
  const [rows, setRows] = useState<CheapestItemScoreRow[]>([]);
  const [status, setStatus] = useState<APIStatus>("pending");

  useEffect(() => {
    let cancelled = false;
    setStatus("pending");
    getCheapestItemScores()
      .then((result) => {
        if (cancelled) return;
        setRows(result);
        setStatus("fulfilled");
      })
      .catch(() => {
        if (!cancelled) setStatus("rejected");
      });
    return () => {
      cancelled = true;
    };
  }, [platform]);

  return (
    <main className="z-10 text-white bg-new-gray-850 flex-1 h-full min-h-screen">
      <HeroSection
        helmetTitle={seo.title}
        helmetDescription={seo.description}
        pageTitle={seo.pageTitle}
        helmetCanonicalLink={canonicalUrl("/cheapest-item-score")}
        shouldBeInTheDocumentFlow
      />
      <section className="mx-auto w-full min-w-0 max-w-[720px] px-4 pb-12 pt-6">
        <p className="text-sm text-foreground-muted">{t("explainer")}</p>
        <div className="mt-6">
          {status === "rejected" ? (
            <p className="text-sm text-foreground-muted">{t("error")}</p>
          ) : (
            <CheapestItemScoreTable
              rows={rows}
              loading={status === "pending"}
            />
          )}
        </div>
      </section>
    </main>
  );
};

export default CheapestItemScorePage;
